import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { StaffLayout } from "@/components/StaffLayout";
import { SuperadminLayout } from "@/components/SuperadminLayout";
import { PWAInstallBanner } from "@/components/PWAInstall";
import { Ticker } from "@/components/Ticker";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import PatientAuth from "@/pages/PatientAuth";
import BrandLogin from "@/pages/BrandLogin";
import { Privacy, Terms, DataDeletion } from "@/pages/Legal";

import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import Analytics from "@/pages/Analytics";
import Staff from "@/pages/Staff";
import RBAC from "@/pages/RBAC";
import Subscription from "@/pages/Subscription";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Billing from "@/pages/Billing";
import ResourceModule from "@/pages/ResourceModule";
import PatientPortal from "@/pages/PatientPortal";

import { SAOverview } from "@/pages/superadmin/SAOverview";
import { SAOrganisations } from "@/pages/superadmin/SAOrganisations";
import { SAPromos } from "@/pages/superadmin/SAPromos";
import { SAPaywall } from "@/pages/superadmin/SAPaywall";
import { SATicker } from "@/pages/superadmin/SATicker";
import { SAPayments } from "@/pages/superadmin/SAPayments";

function RequireStaff({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader full />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.kind === "patient") return <Navigate to="/portal" replace />;
  return children;
}
function RequirePatient({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader full />;
  if (!user) return <Navigate to="/patient" replace />;
  if (user.kind !== "patient") return <Navigate to="/app/dashboard" replace />;
  return children;
}
function RequireSuperadmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader full />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/app/dashboard" replace />;
  return children;
}

// Staff module route: 'billing' → Billing page, everything else → generic ResourceModule
function StaffModule() {
  const { module } = useParams();
  if (module === "billing") return <Billing />;
  return <ResourceModule key={module} />;
}

function StaffApp() {
  return (
    <RequireStaff>
      <StaffLayout>
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="patients" element={<Patients />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="staff" element={<Staff />} />
          <Route path="rbac" element={<RBAC />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path=":module" element={<StaffModule />} />
        </Routes>
      </StaffLayout>
      <PWAInstallBanner />
    </RequireStaff>
  );
}

function SuperadminApp() {
  return (
    <RequireSuperadmin>
      <SuperadminLayout>
        <Routes>
          <Route index element={<SAOverview />} />
          <Route path="organisations" element={<SAOrganisations />} />
          <Route path="promos" element={<SAPromos />} />
          <Route path="paywall" element={<SAPaywall />} />
          <Route path="ticker" element={<SATicker />} />
          <Route path="payments" element={<SAPayments />} />
        </Routes>
      </SuperadminLayout>
    </RequireSuperadmin>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Ticker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/patient" element={<PatientAuth />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/portal" element={<RequirePatient><PatientPortal /></RequirePatient>} />
            <Route path="/app/*" element={<StaffApp />} />
            <Route path="/superadmin/*" element={<SuperadminApp />} />
            <Route path="/:slug/:locality" element={<BrandLogin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}
